import type { AppTheme } from './types';
type SchemeConfig = {
  theme?: AppTheme;
  darkTheme?: AppTheme;
};
const hasEntries = (theme: AppTheme | undefined) => !!theme && Object.keys(theme).length > 0;

/**
 * Whether a config describes both a light and a dark scheme. A non-empty
 * `darkTheme` holds the dark overrides on top of a light `theme`, and the site
 * follows the visitor's system setting; one that overrides nothing carries
 * just `colorScheme: 'dark'`. Without one, `theme` is the only scheme, in the
 * scheme its `colorScheme` names.
 */
export function hasDarkScheme(appConfig: SchemeConfig): boolean {
  return hasEntries(appConfig.darkTheme);
}

/**
 * The scheme a config's `theme` describes on its own: always light under dark
 * overrides, otherwise its `colorScheme`, light when unset.
 */
export function baseColorScheme(appConfig: SchemeConfig): 'light' | 'dark' {
  return !hasDarkScheme(appConfig) && appConfig.theme?.colorScheme === 'dark' ? 'dark' : 'light';
}

/**
 * The scheme to render a config in: the visitor's system setting when the
 * config has both schemes, otherwise the one scheme its `theme` describes.
 */
export function resolveColorScheme(appConfig: SchemeConfig, systemPrefersDark: boolean): 'light' | 'dark' {
  if (hasDarkScheme(appConfig)) {
    return systemPrefersDark ? 'dark' : 'light';
  }
  return baseColorScheme(appConfig);
}